import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layouts/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  MessageSquare, Search, Loader2, Eye, Send, 
  Clock, CheckCircle2, AlertCircle, XCircle,
  RefreshCw, User, Calendar, Paperclip, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSupport = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [responseText, setResponseText] = useState('');
  const [sending, setSending] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/support-tickets`);
      setTickets(response.data);
    } catch (error) {
      toast.error('Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleSendResponse = async () => {
    if (!responseText.trim()) {
      toast.error('Please enter a response');
      return;
    }

    setSending(true);
    try {
      const updated = await axios.post(
        `${API_URL}/api/admin/support-tickets/${selectedTicket.id}/respond`,
        { message: responseText }
      );
      setSelectedTicket(updated.data);
      setResponseText('');
      toast.success('Response sent');
      fetchTickets();
    } catch (error) {
      toast.error('Failed to send response');
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      const updated = await axios.put(
        `${API_URL}/api/admin/support-tickets/${selectedTicket.id}/status?status=${newStatus}`
      );
      setSelectedTicket(updated.data);
      toast.success(`Ticket marked as ${newStatus}`);
      fetchTickets();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const viewTicket = async (ticket) => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/support-tickets/${ticket.id}`);
      setSelectedTicket(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load ticket details');
    }
  };

  const handleDeleteTicket = async () => {
    if (!ticketToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/admin/support-tickets/${ticketToDelete.id}`);
      toast.success('Ticket deleted successfully');
      setDeleteDialogOpen(false);
      setTicketToDelete(null);
      fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete ticket');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-PH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPriorityBadge = (priority) => {
    const config = {
      low: { className: 'bg-gray-500/20 text-gray-400', label: 'Low' },
      normal: { className: 'bg-blue-500/20 text-blue-400', label: 'Normal' },
      high: { className: 'bg-amber-500/20 text-amber-400', label: 'High' },
      urgent: { className: 'bg-red-500/20 text-red-400', label: 'Urgent' }
    };
    const c = config[priority] || config.normal;
    return <Badge className={c.className}>{c.label}</Badge>;
  };

  const getStatusBadge = (status) => {
    const config = {
      open: { icon: AlertCircle, className: 'bg-blue-500/20 text-blue-400', label: 'Open' },
      in_progress: { icon: Clock, className: 'bg-amber-500/20 text-amber-400', label: 'In Progress' },
      resolved: { icon: CheckCircle2, className: 'bg-green-500/20 text-green-400', label: 'Resolved' },
      closed: { icon: XCircle, className: 'bg-gray-500/20 text-gray-400', label: 'Closed' }
    };
    const c = config[status] || config.open;
    const Icon = c.icon;
    return (
      <Badge className={c.className}>
        <Icon className="h-3 w-3 mr-1" />
        {c.label}
      </Badge>
    );
  };

  const getCategoryLabel = (category) => {
    const labels = {
      general: 'General',
      billing: 'Billing',
      technical: 'Technical',
      feature_request: 'Feature Request'
    };
    return labels[category] || category;
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesFilter = filter === 'all' || ticket.status === filter;
    const matchesSearch = !searchTerm || 
      ticket.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.submitter?.email?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const stats = {
    open: tickets.filter(t => t.status === 'open').length,
    in_progress: tickets.filter(t => t.status === 'in_progress').length,
    resolved: tickets.filter(t => t.status === 'resolved').length,
    total: tickets.length
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-support-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-white flex items-center gap-3">
              <MessageSquare className="h-8 w-8 text-indigo-400" />
              Support Tickets
            </h1>
            <p className="text-gray-400 mt-1">Manage and respond to organizer support requests</p>
          </div>
          
          <Button 
            variant="outline" 
            onClick={fetchTickets}
            className="gap-2 border-gray-700 text-gray-300 hover:bg-gray-800"
            data-testid="refresh-tickets-btn"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-400">{stats.open}</p>
              <p className="text-sm text-gray-400">Open</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-amber-400">{stats.in_progress}</p>
              <p className="text-sm text-gray-400">In Progress</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-400">{stats.resolved}</p>
              <p className="text-sm text-gray-400">Resolved</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-gray-400">{stats.total}</p>
              <p className="text-sm text-gray-400">Total</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              <Tabs value={filter} onValueChange={setFilter}>
                <TabsList className="bg-gray-800">
                  <TabsTrigger value="all" className="data-[state=active]:bg-gray-700">All</TabsTrigger>
                  <TabsTrigger value="open" className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400">
                    Open ({stats.open})
                  </TabsTrigger>
                  <TabsTrigger value="in_progress" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400">
                    In Progress
                  </TabsTrigger>
                  <TabsTrigger value="resolved" className="data-[state=active]:bg-green-500/20 data-[state=active]:text-green-400">
                    Resolved
                  </TabsTrigger>
                  <TabsTrigger value="closed" className="data-[state=active]:bg-gray-500/20 data-[state=active]:text-gray-400">
                    Closed
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              
              <div className="relative w-full md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <Input
                  placeholder="Search tickets..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-gray-800 border-gray-700 text-white"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tickets Table */}
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
              </div>
            ) : filteredTickets.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <MessageSquare className="h-12 w-12 mx-auto mb-3 text-gray-700" />
                <p>No support tickets found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-800 hover:bg-gray-800/50">
                    <TableHead className="text-gray-400">Subject</TableHead>
                    <TableHead className="text-gray-400">Submitter</TableHead>
                    <TableHead className="text-gray-400">Category</TableHead>
                    <TableHead className="text-gray-400">Priority</TableHead>
                    <TableHead className="text-gray-400">Status</TableHead>
                    <TableHead className="text-gray-400">Date</TableHead>
                    <TableHead className="text-gray-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTickets.map((ticket) => (
                    <TableRow key={ticket.id} className="border-gray-800 hover:bg-gray-800/50">
                      <TableCell>
                        <div className="max-w-[250px]">
                          <p className="font-medium text-white truncate">{ticket.subject}</p>
                          <p className="text-xs text-gray-500 truncate">{ticket.description}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {ticket.submitter && (
                          <div>
                            <p className="text-gray-300">{ticket.submitter.first_name} {ticket.submitter.last_name}</p>
                            <p className="text-xs text-gray-500">{ticket.submitter.email}</p>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-gray-400">{getCategoryLabel(ticket.category)}</TableCell>
                      <TableCell>{getPriorityBadge(ticket.priority)}</TableCell>
                      <TableCell>{getStatusBadge(ticket.status)}</TableCell>
                      <TableCell className="text-gray-400 text-sm">{formatDate(ticket.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewTicket(ticket)}
                          className="text-gray-400 hover:text-white hover:bg-gray-800"
                          data-testid={`view-ticket-${ticket.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setTicketToDelete(ticket);
                            setDeleteDialogOpen(true);
                          }}
                          className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                          data-testid={`delete-ticket-${ticket.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* View Ticket Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl bg-gray-900 border-gray-800 text-white max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading text-gold flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Ticket Details
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              View and respond to this support ticket
            </DialogDescription>
          </DialogHeader>

          {selectedTicket && (
            <div className="space-y-4 overflow-y-auto flex-1 pr-2">
              {/* Ticket Info */}
              <div className="bg-gray-800 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-lg text-white">{selectedTicket.subject}</h3>
                    <div className="flex items-center gap-2 mt-1 text-sm text-gray-400">
                      <User className="h-3 w-3" />
                      <span>{selectedTicket.submitter?.first_name} {selectedTicket.submitter?.last_name}</span>
                      <span>•</span>
                      <span>{selectedTicket.submitter?.email}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getPriorityBadge(selectedTicket.priority)}
                    {getStatusBadge(selectedTicket.status)}
                  </div>
                </div>
                
                <div className="flex items-center gap-4 text-sm text-gray-400 mb-3">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {formatDate(selectedTicket.created_at)}
                  </span>
                  <span>Category: {getCategoryLabel(selectedTicket.category)}</span>
                </div>

                <p className="text-gray-300 whitespace-pre-wrap">{selectedTicket.description}</p>

                {/* Attachments */}
                {selectedTicket.attachments?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-700">
                    <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                      <Paperclip className="h-3 w-3" />
                      Attachments ({selectedTicket.attachments.length})
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {selectedTicket.attachments.map((att, idx) => (
                        <a 
                          key={idx}
                          href={att.file_data}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-gray-700 px-3 py-1 rounded text-sm hover:bg-gray-600"
                        >
                          {att.file_name}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Status Change */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-400">Change Status:</span>
                <Select
                  value={selectedTicket.status}
                  onValueChange={handleStatusChange}
                  disabled={updatingStatus}
                >
                  <SelectTrigger className="w-40 bg-gray-800 border-gray-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
                {updatingStatus && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
              </div>

              {/* Conversation */}
              {selectedTicket.responses?.length > 0 && (
                <div className="space-y-3">
                  <p className="text-sm text-gray-400 font-medium">Conversation</p>
                  {selectedTicket.responses.map((response, idx) => (
                    <div 
                      key={idx}
                      className={`p-3 rounded-lg ${
                        response.is_admin 
                          ? 'bg-indigo-900/30 border border-indigo-700/50 ml-8' 
                          : 'bg-gray-800 mr-8'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-300">
                          {response.responder_name}
                          {response.is_admin && <Badge className="ml-2 bg-indigo-500/20 text-indigo-400 text-xs">Admin</Badge>}
                        </span>
                        <span className="text-xs text-gray-500">{formatDate(response.created_at)}</span>
                      </div>
                      <p className="text-gray-300 text-sm whitespace-pre-wrap">{response.message}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Reply Form */}
              <div className="space-y-2 pt-2 border-t border-gray-800">
                <p className="text-sm text-gray-400 font-medium">Send Response</p>
                <Textarea
                  value={responseText}
                  onChange={(e) => setResponseText(e.target.value)}
                  placeholder="Type your response..."
                  className="bg-gray-800 border-gray-700 text-white"
                  rows={3}
                  data-testid="ticket-response-input"
                />
                <Button
                  onClick={handleSendResponse}
                  disabled={sending || !responseText.trim()}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="send-response-btn"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                  Send Response
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Ticket Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-md bg-gray-900 border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Delete Support Ticket
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {ticketToDelete && (
            <div className="space-y-4">
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <p className="text-white font-medium">{ticketToDelete.subject}</p>
                <p className="text-gray-400 text-sm mt-1">
                  From: {ticketToDelete.submitter?.first_name} {ticketToDelete.submitter?.last_name}
                </p>
                <p className="text-gray-500 text-xs mt-1">{ticketToDelete.submitter?.email}</p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setDeleteDialogOpen(false);
                    setTicketToDelete(null);
                  }}
                  className="flex-1 border-gray-700 text-gray-300"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDeleteTicket}
                  disabled={deleting}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  data-testid="confirm-delete-ticket"
                >
                  {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                  Delete Ticket
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminSupport;
